<script setup lang="ts">
    import PlannerSlider from '@/views/3d-planner/components/PlannerSlider.vue';
    import GroupLabel from '@/views/3d-planner/components/GroupLabel.vue';
    import GroupSubtitle from '@/views/3d-planner/components/GroupSubtitle.vue';
    import { computed, ref } from 'vue';
    import {
        AlignmentMode,
        alignmentModeToTranslationMode,
        TranslationMode,
    } from '@/domain/anatomy/pelvis/alignment';
    import {
        type AcetabularCupOffset,
        acetabularCupOffsetFromApi,
        type AlignmentCupOffset,
        alignmentCupOffsetFromApi,
        cupWorldOffsetFromUIAcetabular,
        cupWorldOffsetFromUIAlignment,
    } from '@/views/3d-planner/fitting/fittedCup';
    import { useCupOverlayViewModel } from '@/views/3d-planner/cup/useCupOverlayViewModel';
    import { PLANNER_TARGETS } from '@/domain/analytics/analyticsTargets';

    const positionSliderStep = 0.5 as const;

    const sliderMin = -20;
    const sliderMax = 20;

    const cup = useCupOverlayViewModel();

    const mode = ref<TranslationMode>(alignmentModeToTranslationMode(cup.cupAlignMode));

    const alignmentTooltip = computed(() => {
        switch (cup.cupAlignMode) {
            case AlignmentMode.CT:
                return 'CT ';
            case AlignmentMode.APP:
                return 'Anterior Pelvic Plane (APP)';
            case AlignmentMode.FPP:
                return 'Functional Pelvic Plane (FPP)';
            default:
                return '';
        }
    });

    const getCurrentPosition = (): AcetabularCupOffset | AlignmentCupOffset => {
        const fittedCup = cup.fittedCup;
        const templateOffset = cup.templateOffset;

        if (!fittedCup || !templateOffset) {
            throw new Error('Fitted cup is required but not available');
        }

        switch (mode.value) {
            case TranslationMode.ACETABULAR:
                return acetabularCupOffsetFromApi(fittedCup, templateOffset);
            default:
                return alignmentCupOffsetFromApi(fittedCup, templateOffset);
        }
    };

    const getCoordinate = (key: string): number => {
        const position = getCurrentPosition();
        return position[key as keyof typeof position] || 0;
    };

    const setCoordinate = (key: string, value?: number): void => {
        const currentPosition = getCurrentPosition();
        const newPosition = { ...currentPosition, [key]: value };

        const fittedCup = cup.fittedCup;

        if (!fittedCup) {
            throw new Error('Fitted cup is required but not available');
        }

        const newWorldOffset =
            mode.value === TranslationMode.ACETABULAR
                ? cupWorldOffsetFromUIAcetabular(fittedCup, newPosition as AcetabularCupOffset)
                : cupWorldOffsetFromUIAlignment(fittedCup, newPosition as AlignmentCupOffset);
        cup.setWorldOffset(newWorldOffset);
    };

    const toggleOptions = computed(() => [
        {
            label: cup.cupAlignMode,
            value: cup.cupAlignMode,
        },
        {
            label: TranslationMode.ACETABULAR,
            value: TranslationMode.ACETABULAR,
        },
    ]);
</script>

<template>
    <div class="mx-n2 px-2 mt-2">
        <div
            class="my-2 d-flex align-center justify-space-between"
            :data-analytics-id="PLANNER_TARGETS.cupTranslationMode"
        >
            <group-label>Translation Mode</group-label>
            <v-icon
                size="20"
                color="rgb(var(--v-theme-secondary))"
                icon="mdi-information-outline"
            />
            <interactive-tooltip
                location="right"
                activator="parent"
                add-content-class="translation-mode-tooltip"
                class="ml-3"
            >
                <div class="cup-position-tooltip">
                    <p>Adjusting position along Acetabular or {{ alignmentTooltip }} directions.</p>
                </div>
            </interactive-tooltip>
        </div>

        <div class="mb-5">
            <app-toggle
                v-model="mode"
                :options="toggleOptions"
                data-testid="translation-mode-toggle"
            />
        </div>

        <!-- Acetabular Controls -->
        <template v-if="mode === TranslationMode.ACETABULAR">
            <div class="mb-2 input-group">
                <group-subtitle>Depth (mm)</group-subtitle>
                <PlannerSlider
                    :model-value="getCoordinate('depth')"
                    :min="sliderMin"
                    :max="sliderMax"
                    :step="positionSliderStep"
                    @update:model-value="(value) => setCoordinate('depth', value)"
                />
            </div>

            <div class="mb-2 input-group">
                <group-subtitle>Forward (mm)</group-subtitle>
                <PlannerSlider
                    :model-value="getCoordinate('ap')"
                    :min="sliderMin"
                    :max="sliderMax"
                    :step="positionSliderStep"
                    @update:model-value="(value) => setCoordinate('ap', value)"
                />
            </div>

            <div class="mb-2 input-group">
                <group-subtitle>Upward (mm)</group-subtitle>
                <PlannerSlider
                    :model-value="getCoordinate('si')"
                    :min="sliderMin"
                    :max="sliderMax"
                    :step="positionSliderStep"
                    @update:model-value="(value) => setCoordinate('si', value)"
                />
            </div>
        </template>

        <!-- Alignment Controls -->
        <template v-else>
            <div class="mb-2 input-group">
                <group-subtitle>Medial - Lateral (mm)</group-subtitle>
                <PlannerSlider
                    :model-value="getCoordinate('ml')"
                    :min="sliderMin"
                    :max="sliderMax"
                    :step="positionSliderStep"
                    @update:model-value="(value) => setCoordinate('ml', value)"
                />
            </div>

            <div class="mb-2 input-group">
                <group-subtitle>Anterior - Posterior (mm)</group-subtitle>
                <PlannerSlider
                    :model-value="getCoordinate('ap')"
                    :min="sliderMin"
                    :max="sliderMax"
                    :step="positionSliderStep"
                    @update:model-value="(value) => setCoordinate('ap', value)"
                />
            </div>

            <div class="mb-2 input-group">
                <group-subtitle>Superior - Inferior (mm)</group-subtitle>
                <PlannerSlider
                    :model-value="getCoordinate('si')"
                    :min="sliderMin"
                    :max="sliderMax"
                    :step="positionSliderStep"
                    @update:model-value="(value) => setCoordinate('si', value)"
                />
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
    .cup-position-tooltip {
        max-width: 308px;
        font-weight: 400;
        font-size: 16px;
        line-height: 26px;
        color: rgb(var(--v-theme-secondary-text));
        padding: 26px 25px;
    }
</style>
